import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Heading, isValidCertificationType } from '../../components';

import css from './ListingPage.module.css';

/**
 * The SectionCertificationsMaybe component.
 * Renders the sitter's self-reported certifications (max one per type) as rows with
 * their type icon. Certificates that have a photo/document link to the full-size file.
 * The section is always rendered — it just stays empty when nothing has been added.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Object} props.publicData - The listing's public data
 * @returns {JSX.Element} section certifications
 */
const SectionCertificationsMaybe = props => {
  const { className, rootClassName, publicData } = props;
  const intl = useIntl();
  const certifications = (publicData?.certifications || []).filter(certification =>
    isValidCertificationType(certification?.type)
  );

  const classes = classNames(rootClassName || css.sectionCertifications, className);

  return (
    <section className={classes}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        <FormattedMessage id="ListingPage.certificationsTitle" />
      </Heading>

      {certifications.length === 0 ? (
        <p className={css.certificationsEmpty}>
          <FormattedMessage id="ListingPage.certificationsEmpty" />
        </p>
      ) : (
        <ul className={css.certificationList}>
          {certifications.map((certification, index) => {
            const { type, name, issuer, docUrl, docName } = certification;
            const typeLabel = intl.formatMessage({
              id: `EditListingDetailsForm.certifications.type.${type}`,
            });

            const content = (
              <>
                <span className={css.certificationTag} title={typeLabel}>
                  <FormattedMessage id="ListingPage.profile.certTag" />
                </span>
                <span className={css.certificationInfo}>
                  <span className={css.certificationName}>{name}</span>
                  <span className={css.certificationMeta}>{issuer || typeLabel}</span>
                </span>
                <span className={css.certificationBadge}>
                  <FormattedMessage id="ListingPage.certificationSelfReported" />
                </span>
              </>
            );

            return (
              <li key={`${type}_${index}`}>
                {docUrl ? (
                  <a
                    className={classNames(css.certificationRow, css.certificationRowLink)}
                    href={docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={docName || name}
                  >
                    {content}
                  </a>
                ) : (
                  <div className={css.certificationRow}>{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default SectionCertificationsMaybe;
